import { PageHeader, Stat, Stats } from '../../components/office';
import { useState, useEffect } from 'react';
import { dashboardApi, reportsApi } from '@verlo/shared/services/api';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Button } from '@verlo/shared/components/ui/button';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { toast } from 'sonner';

// One section style for the page (Back office sweep, Oct 2026: the older styling).
const Section = ({ title, children, testId }) => (
  <section className="rounded-lg border border-hairline bg-white" data-testid={testId}>
    <h2 className="border-b border-hairline px-5 py-3 font-heading text-[15px] font-extrabold text-ink">{title}</h2>
    {children}
  </section>
);

const SalesPage = () => {
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [month, setMonth] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const weekStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const monthStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const [statsRes, weekRes, monthRes, catRes] = await Promise.all([
        dashboardApi.getStats(),
        reportsApi.getYoYComparison(weekStart, endDate),
        reportsApi.getYoYComparison(monthStart, endDate),
        reportsApi.getSalesByCategory(weekStart, endDate, null, 7),
      ]);

      setToday(statsRes.data);
      setWeek(weekRes.data?.current || null);
      setMonth(monthRes.data?.current || null);
      setTopSellers(catRes.data?.top_items || []);
    } catch (err) {
      toast.error("Couldn't load sales data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="sales-page">
        <Skeleton className="h-9 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const weekTotal = week?.revenue ?? 0;
  const monthTotal = month?.revenue ?? 0;
  const hasAnySales = (today?.total_sales_today ?? 0) > 0 || weekTotal > 0 || monthTotal > 0;

  return (
    <div className="space-y-6" data-testid="sales-page">
      <PageHeader
        title="Sales"
        purpose="Every order the tills took, searchable by day, table and status."
        actions={<Button variant="outline" onClick={fetchData}>Refresh</Button>}
      />

      {!hasAnySales && (
        <Notice tone="info" word="None yet" data-testid="sales-empty">
          No sales recorded yet. The figures appear here once orders are paid.
        </Notice>
      )}

      <Stats>
        <Stat label="Today's takings" value={formatCurrency(today?.total_sales_today)} testId="sales-total" />
        <Stat label="Orders today" value={today?.orders_today ?? 0} testId="sales-orders" />
        <Stat label="Average order" value={formatCurrency(today?.avg_order_value)} testId="sales-avg" />
        <Stat label="Last 7 days" value={formatCurrency(weekTotal)} testId="sales-week-total" />
      </Stats>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Section title="How people paid today">
          <dl className="divide-y divide-hairline">
            <div className="flex items-center justify-between px-5 py-3">
              <dt className="text-[14px] text-ink">Card</dt>
              <dd className="font-mono tabular-nums text-[14px] text-ink" data-testid="sales-card-total">{formatCurrency(today?.card_sales_today)}</dd>
            </div>
            <div className="flex items-center justify-between px-5 py-3">
              <dt className="text-[14px] text-ink">Cash</dt>
              <dd className="font-mono tabular-nums text-[14px] text-ink" data-testid="sales-cash-total">{formatCurrency(today?.cash_sales_today)}</dd>
            </div>
          </dl>
        </Section>

        <Section title="Best sellers, last 7 days">
          {topSellers.length === 0 ? (
            <p className="px-5 py-6 text-[14px] text-ink-soft" data-testid="sales-top-seller-empty">No sales in this period.</p>
          ) : (
            <ol className="divide-y divide-hairline">
              {topSellers.slice(0, 5).map((item, index) => (
                <li key={item.name} className="flex items-center justify-between gap-3 px-5 py-2.5" data-testid="sales-top-seller-row">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="w-4 text-right font-mono tabular-nums text-[13px] text-ink-faint">{index + 1}</span>
                    <span className="truncate text-[14px] text-ink">{item.name}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block font-mono tabular-nums text-[14px] text-ink">{formatCurrency(item.revenue)}</span>
                    <span className="block text-[12.5px] text-ink-faint">{item.quantity} sold</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Section>
      </div>

      <Section title="Last 30 days">
        <Stats className="p-5">
          <Stat label="Takings" value={formatCurrency(monthTotal)} testId="sales-month-total" />
          <Stat label="Orders" value={month?.order_count ?? 0} testId="sales-month-orders" />
          <Stat label="Average order" value={formatCurrency(month?.avg_order_value)} testId="sales-month-avg" />
        </Stats>
      </Section>
    </div>
  );
};

export default SalesPage;
